import { useEffect, useRef } from "react";

/**
 * Loads the next page when a sentinel scrolls into view.
 *
 * Replaces a "load older" button: a director working down an event log shouldn't have to keep
 * reaching for the same control. `rootMargin` fires it slightly early so the next page is usually
 * already there by the time the reader arrives.
 */
export function useInfiniteScroll(
    onLoadMore: () => void,
    { enabled, isLoading }: { enabled: boolean; isLoading: boolean },
): React.RefObject<HTMLDivElement> {
    const sentinel = useRef<HTMLDivElement>(null);
    // Kept in a ref so the observer doesn't need re-creating every time the callback identity moves.
    const handler = useRef(onLoadMore);
    handler.current = onLoadMore;

    useEffect(() => {
        const node = sentinel.current;
        if (!node || !enabled || isLoading) return;
        const observer = new IntersectionObserver(
            (entries) => {
                if (entries.some((e) => e.isIntersecting)) handler.current();
            },
            { rootMargin: "400px 0px" },
        );
        observer.observe(node);
        return () => observer.disconnect();
    }, [enabled, isLoading]);

    return sentinel;
}
